import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
    Box, Button, Divider, Grid, IconButton, Paper,
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { Add, Delete, UploadFile } from "@mui/icons-material";
import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useAuth } from "hooks/useAuth";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useDOUploadFile } from "hooks";
import defaultLogoSrc from "assets/TMS-NepalCan-Logo-Quotation.png";
import { generateVendorQuotationExcel } from "utils/generateVendorQuotationExcel";

// ─── Default Data ──────────────────────────────────────────────────────────────

const INITIAL_STATE = {
    account: null,
    entity: null,
    title: "",
    isActive: true,
    logoFile: null,

    // Header
    headerCompanyName: "Nepal Can Logistics Pvt. Ltd.",
    headerFormerName: "(Formerly known as Nepal Can Transport Pvt. Ltd.)",
    headerRegnNo: "Regd no. 306615/079/080",
    headerPanNo: "PAN no. 610442580",

    // Quotation
    date: "", toName: "", toAddress: "",
    subject: "Service Quotation",

    // Grid
    brands: [{ name: "", products: [{ name: "" }] }],
    locations: [""],

    chargesNote: "*Charges are commercially confidential.",

    // Terms
    termsAndConditions: [
        "Quoted rates Excluding 13% VAT.",
        "The Consignor is solely responsible for insuring the consignment to cover all risks of loss or damage, as the transport company will not be liable for such claims.",
        "The Consignor must ensure full compliance with all government/customs rules. The transport company is not responsible for goods declared contraband or subsequently confiscated by the Government.",
        "This quotation is valid for a period of 7 days only",
        "Rates remain stable within a Rs±10 fluctuation in fuel prices; any variation beyond this range may lead to revision upon mutual agreement.",
    ],
    
    // Closing
    closingNote: "Please feel free to write to us if you have any further queries about it.",
    thankYouNote: "Thank You.",
    regards: "With Regards,",
    signatoryName: "", signatoryDesignation: "",
    signatoryCompany: "Nepal Can Logistics Pvt. Ltd.",
    signatoryAddress: "Jadibuti, Kathmandu, Nepal",
    signatoryPhone: "", signatoryEmail: "",

    // Footer
    footerLeft: "Muni Bhairab Marga, Tinkune,\nKMC-32, Kathmandu",
    footerCenter: "01-5970736",
    footerRightWeb: "www.logistics.nepalcan.com",
    footerRightEmail: "care@logistics.nepalcan.com",
};

const cellKey = (location, brandName, productName) =>
    `${location}|||${brandName}|||${productName}`;

const SectionHeading = ({ children }) => (
    <Grid item xs={12} sx={{ mt: 1 }}>
        <Divider sx={{ mb: 1.5 }} />
        <Typography variant="subtitle1" fontWeight={700} color="primary">{children}</Typography>
    </Grid>
);

// ─── Main Form ─────────────────────────────────────────────────────────────────

const VendorQuotationExcelForm = ({
    isBusy = false,
    priceListDetail = null,
    onSubmit = () => { },
}) => {
    const navigate = useNavigate();
    const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();
    const logoInputRef = useRef(null);
    const isMounted = useRef(true);
    const { handleUploadFiles, uploadingFiles } = useDOUploadFile();
    const [logoPreview, setLogoPreview] = useState(defaultLogoSrc);
    const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
    const [gridValues, setGridValues] = useState({});

    const { data: accounts, isLoading: loadingAccounts } = useGetDDLAccountListQuery();
    const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
        useLazyGetDDLEntityListQuery();

    const { control, handleSubmit, setValue, reset, watch, formState: { errors } } = useForm({
        defaultValues: {
            ...INITIAL_STATE,
            entity: viewEntity?.entity || null,
            account: viewEntity?.entity?.account || null,
        },
        mode: "onChange",
    });

    const _watchAccount = useWatch({ control, name: "account" });
    const brands = watch("brands");
    const locations = watch("locations");
    const termsAndConditions = watch("termsAndConditions");

    // ── Effects ──
    useEffect(() => {
        if (!priceListDetail) {
            reset({
                ...INITIAL_STATE,
                entity: viewEntity?.entity || null,
                account: viewEntity?.entity?.account || null,
            });
            setGridValues({});
            setLogoPreview(defaultLogoSrc);
            return;
        }
        reset({
            ...INITIAL_STATE,
            ...priceListDetail,
            logoFile: null,
            account: priceListDetail.account || null,
            entity: priceListDetail.entity || null,
            brands: priceListDetail.brands?.length
                ? priceListDetail.brands
                : [{ name: "", products: [{ name: "" }] }],
            locations: priceListDetail.locations?.length
                ? priceListDetail.locations
                : [""],
            termsAndConditions: priceListDetail.termsAndConditions?.length
                ? priceListDetail.termsAndConditions
                : INITIAL_STATE.termsAndConditions,
        });
        const gv = {};
        (priceListDetail.entries || []).forEach((e) => {
            gv[cellKey(e.location, e.brandName, e.productName)] = e.value;
        });
        setGridValues(gv);
        setLogoPreview(defaultLogoSrc);
        return () => { isMounted.current = false; };
    }, [priceListDetail, reset, viewEntity]);

    useEffect(() => {
        if ((isAccountAdmin || isSystemAdmin) && _watchAccount?._id && isMounted.current) {
            fetchEntities({ account: _watchAccount._id });
        }
    }, [_watchAccount?._id, fetchEntities, isAccountAdmin, isSystemAdmin]);

    // ── Logo ──
    const handleLogoSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setValue("logoFile", file);
        setLogoPreview(URL.createObjectURL(file));
    };
    const handleLogoReset = () => {
        setValue("logoFile", null);
        setLogoPreview(defaultLogoSrc);
        if (logoInputRef.current) logoInputRef.current.value = "";
    };

    // ── Brand helpers ──
    const addBrand = () =>
        setValue("brands", [...brands, { name: "", products: [{ name: "" }] }]);
    const removeBrand = (bIdx) =>
        setValue("brands", brands.filter((_, i) => i !== bIdx));
    const updateBrandName = (bIdx, value) =>
        setValue("brands", brands.map((b, i) => (i === bIdx ? { ...b, name: value } : b)));

    // ── Product helpers ──
    const addProduct = (bIdx) =>
        setValue("brands", brands.map((b, i) =>
            i === bIdx ? { ...b, products: [...b.products, { name: "" }] } : b));
    const removeProduct = (bIdx, pIdx) =>
        setValue("brands", brands.map((b, i) =>
            i === bIdx ? { ...b, products: b.products.filter((_, j) => j !== pIdx) } : b));
    const updateProductName = (bIdx, pIdx, value) =>
        setValue("brands", brands.map((b, i) =>
            i === bIdx
                ? { ...b, products: b.products.map((p, j) => (j === pIdx ? { name: value } : p)) }
                : b));

    // ── Location helpers ──
    const addLocation = () => setValue("locations", [...locations, ""]);
    const removeLocation = (idx) => setValue("locations", locations.filter((_, i) => i !== idx));
    const updateLocation = (idx, value) =>
        setValue("locations", locations.map((l, i) => (i === idx ? value : l)));

    // ── Grid helpers ──
    const handleCellChange = (location, brandName, productName, value) =>
        setGridValues((prev) => ({ ...prev, [cellKey(location, brandName, productName)]: value }));

    const buildEntries = () => {
        const result = [];
        locations.filter(Boolean).forEach((location) => {
            brands.forEach((brand) => {
                brand.products.forEach((product) => {
                    const value = gridValues[cellKey(location, brand.name, product.name)] ?? "";
                    if (value.trim()) {
                        result.push({ location, brandName: brand.name, productName: product.name, value });
                    }
                });
            });
        });
        return result;
    };

    // ── Terms helpers ──
    const addTerm = () => setValue("termsAndConditions", [...termsAndConditions, ""]);
    const removeTerm = (idx) =>
        setValue("termsAndConditions", termsAndConditions.filter((_, i) => i !== idx));
    const updateTerm = (idx, value) =>
        setValue("termsAndConditions", termsAndConditions.map((t, i) => (i === idx ? value : t)));

    // ── Submit ──
    const handleOnSubmit = async (data) => {
        setIsGeneratingExcel(true);
        try {
            const entries = buildEntries();
            const fullData = { ...data, entries };

            const wb = await generateVendorQuotationExcel(fullData);
            const buffer = await wb.xlsx.writeBuffer();
            const fileName = `${data.title || "price-list"}.xlsx`;

            const excelBlob = new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const excelFile = new File([excelBlob], fileName, { type: excelBlob.type });

            const { uploadedFiles } = await handleUploadFiles([excelFile], {
                folderName: "PriceList",
                entity: viewEntity?.entity?._id,
            });

            onSubmit({
                ...fullData,
                logoFile: undefined,
                account: data?.account?._id,
                entity: data?.entity?._id,
                excelAttachment: uploadedFiles[0],
            });
        } catch (err) {
            console.error("Excel generation/upload error:", err);
        } finally {
            setIsGeneratingExcel(false);
        }
    };

    const isSubmitting = isBusy || isGeneratingExcel || uploadingFiles;
    const flatProducts = brands.flatMap((b) => b.products.map((p) => ({ brandName: b.name, productName: p.name })));
    const visibleLocations = locations.filter(Boolean);
    const showGrid = flatProducts.length > 0 && visibleLocations.length > 0;

    // ── Render ──────────────────────────────────────────────────────────────────
    return (
        <AppForm loading={isSubmitting}>
            <AppForm.Content>
                <Grid container spacing={3}>

                    {/* ── Account & Entity ─────────────────────────────────────────── */}
                    {(isAccountAdmin || isSystemAdmin) && (
                        <>
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="account" control={control}
                                    rules={{ required: "Please select an account." }}
                                    render={({ field }) => (
                                        <BaseAutocomplete
                                            {...field} fullWidth required label="Account"
                                            value={field.value ?? null}
                                            onChange={(_, data) => { field.onChange(data); setValue("entity", null); }}
                                            getOptionLabel={(o) => o?.name ?? ""}
                                            isOptionEqualToValue={(o, v) => o?._id === v?._id}
                                            options={accounts || []} loading={loadingAccounts}
                                            loadingText="Loading Account..." disabled={isAccountAdmin}
                                            helperText={errors?.account?.message} error={!!errors?.account}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="entity" control={control}
                                    rules={{ required: "Please select an entity." }}
                                    render={({ field: { value, onChange } }) => (
                                        <BaseAutocomplete
                                            fullWidth required label="Entity"
                                            value={value ?? null}
                                            onChange={(_, data) => onChange(data)}
                                            getOptionLabel={(o) => o?.name ?? ""}
                                            isOptionEqualToValue={(o, v) => o?._id === v?._id}
                                            options={entities || []} loading={loadingEntities}
                                            loadingText="Loading Entities..."
                                            helperText={errors?.entity?.message} error={!!errors?.entity}
                                        />
                                    )}
                                />
                            </Grid>
                        </>
                    )}

                    {/* ── Title ────────────────────────────────────────────────────── */}
                    <Grid item xs={12}>
                        <Controller
                            name="title" control={control}
                            rules={{ required: "Title is required." }}
                            render={({ field }) => (
                                <TextField {...field} fullWidth required size="small" label="Title"
                                    error={!!errors.title} helperText={errors.title?.message} />
                            )}
                        />
                    </Grid>

                    {/* ── Excel Header ─────────────────────────────────────────────── */}
                    <SectionHeading>Excel Header</SectionHeading>

                    <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" mb={1}>Logo</Typography>
                        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                            <Box component="img" src={logoPreview} alt="Logo Preview"
                                sx={{
                                    height: 64, maxWidth: 180, objectFit: "contain",
                                    border: "1px solid", borderColor: "divider",
                                    borderRadius: 1, p: 0.5, background: "#fff",
                                }}
                            />
                            <Box display="flex" gap={1}>
                                <input type="file" accept="image/*" ref={logoInputRef}
                                    style={{ display: "none" }} onChange={handleLogoSelect} />
                                <Button size="small" variant="outlined" startIcon={<UploadFile />}
                                    onClick={() => logoInputRef.current?.click()}>
                                    Change Logo
                                </Button>
                                <Button size="small" color="error" onClick={handleLogoReset}>
                                    Reset to Default
                                </Button>
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Controller name="headerCompanyName" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Company Name" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="headerFormerName" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Formerly Known As" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="headerRegnNo" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Regd No." />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="headerPanNo" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="PAN No." />
                            )} />
                    </Grid>

                    {/* ── Quotation Details ────────────────────────────────────────── */}
                    <SectionHeading>Quotation Details</SectionHeading>

                    <Grid item xs={12} md={4}>
                        <Controller
                            name="date" control={control}
                            rules={{ required: "Date is required." }}
                            render={({ field }) => (
                                <TextField {...field} fullWidth required type="date" size="small" label="Date"
                                    InputLabelProps={{ shrink: true }}
                                    error={!!errors.date} helperText={errors.date?.message} />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Controller name="subject" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Subject" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="toName" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="To — Name" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="toAddress" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="To — Address" />
                            )} />
                    </Grid>

                    {/* ── Brands & Products ─────────────────────────────────────────── */}
                    <SectionHeading>Brands &amp; Products</SectionHeading>

                    {brands.map((brand, bIdx) => (
                        <Grid item xs={12} key={bIdx}>
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography variant="subtitle2" fontWeight={600}>Brand {bIdx + 1}</Typography>
                                    {brands.length > 1 && (
                                        <IconButton size="small" color="error" onClick={() => removeBrand(bIdx)}>
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                                <TextField fullWidth size="small" label="Brand Name"
                                    value={brand.name}
                                    onChange={(e) => updateBrandName(bIdx, e.target.value)}
                                    sx={{ mb: 2 }} />
                                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                    Products
                                </Typography>
                                {brand.products.map((product, pIdx) => (
                                    <Box key={pIdx} display="flex" alignItems="center" gap={1} mb={1}>
                                        <TextField size="small" label={`Product ${pIdx + 1}`} sx={{ flex: 1 }}
                                            value={product.name}
                                            onChange={(e) => updateProductName(bIdx, pIdx, e.target.value)} />
                                        <IconButton size="small" color="error"
                                            onClick={() => removeProduct(bIdx, pIdx)}
                                            disabled={brand.products.length === 1}>
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ))}
                                <Button size="small" startIcon={<Add />}
                                    onClick={() => addProduct(bIdx)} sx={{ mt: 0.5 }}>
                                    Add Product
                                </Button>
                            </Paper>
                        </Grid>
                    ))}
                    <Grid item xs={12}>
                        <Button variant="outlined" size="small" startIcon={<Add />} onClick={addBrand}>
                            Add Brand
                        </Button>
                    </Grid>

                    {/* ── Locations ─────────────────────────────────────────────────── */}
                    <SectionHeading>Locations</SectionHeading>

                    {locations.map((loc, idx) => (
                        <Grid item xs={12} md={4} key={idx}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <TextField fullWidth size="small" label={`Location ${idx + 1}`}
                                    value={loc} onChange={(e) => updateLocation(idx, e.target.value)} />
                                <IconButton size="small" color="error"
                                    onClick={() => removeLocation(idx)} disabled={locations.length === 1}>
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Box>
                        </Grid>
                    ))}
                    <Grid item xs={12}>
                        <Button variant="outlined" size="small" startIcon={<Add />} onClick={addLocation}>
                            Add Location
                        </Button>
                    </Grid>

                    {/* ── Price Grid ────────────────────────────────────────────────── */}
                    {showGrid && (
                        <>
                            <SectionHeading>Price Grid</SectionHeading>
                            <Grid item xs={12}>
                                <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
                                    <Table size="small" sx={{ minWidth: 500 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell rowSpan={2}
                                                    sx={{
                                                        fontWeight: 700, minWidth: 140, bgcolor: "action.hover",
                                                        borderRight: "2px solid", borderColor: "divider"
                                                    }}>
                                                    Location
                                                </TableCell>
                                                {brands.map((brand, bIdx) =>
                                                    brand.products.length > 0 ? (
                                                        <TableCell key={bIdx} colSpan={brand.products.length} align="center"
                                                            sx={{
                                                                fontWeight: 700, bgcolor: "action.hover",
                                                                borderLeft: "2px solid", borderColor: "divider"
                                                            }}>
                                                            {brand.name || `Brand ${bIdx + 1}`}
                                                        </TableCell>
                                                    ) : null
                                                )}
                                            </TableRow>
                                            <TableRow>
                                                {flatProducts.map((fp, pIdx) => (
                                                    <TableCell key={pIdx}
                                                        sx={{
                                                            fontSize: "0.7rem", maxWidth: 130,
                                                            whiteSpace: "normal", wordBreak: "break-word",
                                                            bgcolor: "action.hover",
                                                            borderLeft: pIdx === 0 || flatProducts[pIdx - 1]?.brandName !== fp.brandName
                                                                ? "2px solid" : undefined,
                                                            borderColor: "divider"
                                                        }}>
                                                        {fp.productName || `Product ${pIdx + 1}`}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {visibleLocations.map((location, lIdx) => (
                                                <TableRow key={lIdx}
                                                    sx={{ "&:nth-of-type(even)": { bgcolor: "action.hover" } }}>
                                                    <TableCell sx={{
                                                        fontWeight: 600, whiteSpace: "nowrap",
                                                        borderRight: "2px solid", borderColor: "divider"
                                                    }}>
                                                        {location}
                                                    </TableCell>
                                                    {flatProducts.map((fp, pIdx) => (
                                                        <TableCell key={pIdx}
                                                            sx={{
                                                                p: 0.5,
                                                                borderLeft: pIdx === 0 || flatProducts[pIdx - 1]?.brandName !== fp.brandName
                                                                    ? "2px solid" : undefined,
                                                                borderColor: "divider"
                                                            }}>
                                                            <TextField size="small" variant="outlined"
                                                                value={gridValues[cellKey(location, fp.brandName, fp.productName)] ?? ""}
                                                                onChange={(e) => handleCellChange(location, fp.brandName, fp.productName, e.target.value)}
                                                                inputProps={{ style: { padding: "4px 6px", width: 72 } }} />
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>
                        </>
                    )}

                    {/* ── Charges Note ─────────────────────────────────────────────── */}
                    <SectionHeading>Charges Note</SectionHeading>
                    <Grid item xs={12}>
                        <Controller name="chargesNote" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Charges Note"
                                    helperText="Appears below the table with a yellow highlight" />
                            )} />
                    </Grid>

                    {/* ── Terms & Conditions ───────────────────────────────────────── */}
                    <SectionHeading>Terms &amp; Conditions</SectionHeading>

                    {termsAndConditions?.map((term, idx) => (
                        <Grid item xs={12} key={idx}>
                            <Box display="flex" alignItems="flex-start" gap={1}>
                                <Typography variant="body2" sx={{ mt: 1, minWidth: 22, fontWeight: 600 }}>
                                    {idx + 1}.
                                </Typography>
                                <TextField fullWidth size="small" multiline label={`Term ${idx + 1}`}
                                    value={term} onChange={(e) => updateTerm(idx, e.target.value)} />
                                <IconButton size="small" color="error"
                                    onClick={() => removeTerm(idx)}
                                    disabled={termsAndConditions.length === 1} sx={{ mt: 0.5 }}>
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Box>
                        </Grid>
                    ))}
                    <Grid item xs={12}>
                        <Button size="small" startIcon={<Add />} onClick={addTerm}>Add Term</Button>
                    </Grid>

                    {/* ── Closing & Signatory ──────────────────────────────────────── */}
                    <SectionHeading>Closing &amp; Signatory</SectionHeading>

                    <Grid item xs={12}>
                        <Controller name="closingNote" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Closing Note" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="thankYouNote" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Thank You Text" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="regards" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Regards Text" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="signatoryName" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Signatory Name" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="signatoryDesignation" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Designation" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="signatoryCompany" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Company" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="signatoryAddress" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Address" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="signatoryPhone" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Phone" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="signatoryEmail" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Email" />
                            )} />
                    </Grid>

                    {/* ── Excel Footer ─────────────────────────────────────────────── */}
                    <SectionHeading>Excel Footer</SectionHeading>

                    <Grid item xs={12} md={6}>
                        <Controller name="footerLeft" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" multiline rows={2}
                                    label="Footer Left"
                                    helperText={`Use \\n for a new line (e.g. "Line 1\\nLine 2")`} />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="footerCenter" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Footer Center (Phone)" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="footerRightWeb" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Footer Right — Website" />
                            )} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller name="footerRightEmail" control={control}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" label="Footer Right — Email" />
                            )} />
                    </Grid>

                    {/* ── Active toggle (edit mode only) ───────────────────────────── */}
                    {priceListDetail && (
                        <Grid item xs={6}>
                            <Controller name="isActive" control={control}
                                render={({ field }) => <AppSwitch {...field} label="Is Active?" />} />
                        </Grid>
                    )}
                </Grid>
            </AppForm.Content>

            <AppForm.Actions>
                <Button onClick={() => navigate(-1)} disabled={isSubmitting}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit(handleOnSubmit)} disabled={isSubmitting}>
                    {isGeneratingExcel
                        ? "Generating Excel…"
                        : isBusy
                            ? "Saving…"
                            : priceListDetail
                                ? "Update Vendor Quotation Excel"
                                : "Add Vendor Quotation Excel"}
                </Button>
            </AppForm.Actions>
        </AppForm>
    );
};

export default VendorQuotationExcelForm;